import { Button } from '@alison/ui';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useDeliveryLog } from '../../../lib/automations.hooks.js';
import { humanize } from '../catalogFilters.js';
import { emailDeliveryLabel, runAtLabel, slackDeliveryLabel } from './automationText.js';

const HEADINGS = ['Run', 'Finished', 'Status', 'Email', 'Slack'];

const HEAD_SX = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'text.disabled',
};

interface DeliveryLogDialogProps {
  automation: { id: string; name: string };
  onClose: () => void;
}

export function DeliveryLogDialog({ automation, onClose }: DeliveryLogDialogProps): ReactNode {
  const log = useDeliveryLog(automation.id);
  const occurrences = log.data?.occurrences ?? [];

  const body = (): ReactNode => {
    if (log.isPending) {
      return (
        <Stack sx={{ alignItems: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      );
    }
    if (log.isError) return <Alert severity="error">The delivery log could not be loaded.</Alert>;
    if (occurrences.length === 0) {
      return (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          This automation has not run yet.
        </Typography>
      );
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            {HEADINGS.map((heading) => (
              <TableCell key={heading} sx={HEAD_SX}>
                {heading}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {occurrences.map((occurrence) => (
            <TableRow key={occurrence.id} data-testid={`delivery-row-${occurrence.id}`}>
              <TableCell>
                {occurrence.label ?? (occurrence.seq === null ? '—' : `#${occurrence.seq}`)}
              </TableCell>
              <TableCell>
                {occurrence.completedAt === null ? '—' : runAtLabel(occurrence.completedAt)}
              </TableCell>
              <TableCell>{humanize(occurrence.status)}</TableCell>
              <TableCell>{emailDeliveryLabel(occurrence)}</TableCell>
              <TableCell>{slackDeliveryLabel(occurrence)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Delivery log · {automation.name}</DialogTitle>
      <DialogContent>{body()}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
